/**
 * Accounts Payable Report Page
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, CreditCard } from 'lucide-react'

import api from '@/lib/api'
import { formatCurrency, formatDate, cn } from '@/lib/utils'
import { useTranslation } from '@/hooks/useTranslation'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ExportButtons } from '@/components/ui/export-buttons'
import { DatePicker } from '@/components/ui/date-picker'
import { QueryError } from '@/components/ui/query-error'

interface PayableItem {
  purchase_id: number
  provider_name: string
  invoice_number: string | null
  purchase_date: string | null
  invoice_date: string | null
  purchase_total: number
  total_deposited: number
  remaining: number
  days_old: number | null
}

interface PayablesData {
  items: PayableItem[]
  total_payable: number
  count: number
}

export function AccountsPayableReport() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const hasDateFilter = !!(dateFrom || dateTo)

  const { data: payables, isLoading, isError, refetch } = useQuery({
    queryKey: ['reports-accounts-payable', dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (dateFrom) params.set('date_from', dateFrom)
      if (dateTo) params.set('date_to', `${dateTo}T23:59:59`)
      const qs = params.toString()
      const response = await api.get(`/reports/accounts-payable${qs ? `?${qs}` : ''}`)
      return response.data as PayablesData
    },
  })

  // Calculate totals
  const totalAmount = payables?.items?.reduce((sum, item) => sum + item.purchase_total, 0) || 0
  const totalDeposited = payables?.items?.reduce((sum, item) => sum + item.total_deposited, 0) || 0

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate('/reports')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold">{t('reports.accountsPayable')}</h1>
          <p className="text-sm text-muted-foreground">{t('reports.accountsPayableSubtitle')}</p>
        </div>
        {hasDateFilter && (
          <ExportButtons
            data={payables?.items || []}
            filename="cuentas-por-pagar"
            columns={[
              { key: 'provider_name', header: t('articles.provider') },
              { key: 'invoice_number', header: t('reports.invoiceNumber') },
              { key: 'invoice_date', header: t('reports.purchaseDate'), format: (v) => v ? formatDate(v as string) : '' },
              { key: 'purchase_date', header: t('reports.registrationDate'), format: (v) => v ? formatDate(v as string) : '' },
              { key: 'purchase_total', header: t('reports.total'), format: (v) => formatCurrency((v as number) || 0) },
              { key: 'total_deposited', header: t('reports.deposited'), format: (v) => formatCurrency((v as number) || 0) },
              { key: 'remaining', header: t('reports.balance'), format: (v) => formatCurrency((v as number) || 0) },
              { key: 'days_old', header: t('reports.daysOld'), format: (v) => (v == null ? '' : `${v}`) },
            ]}
          />
        )}
      </div>

      {/* Date range filter */}
      <div className="grid grid-cols-2 gap-2 md:max-w-md">
        <DatePicker
          value={dateFrom}
          onChange={setDateFrom}
          max={dateTo || undefined}
          placeholder={t('reports.from')}
          ariaLabel={t('reports.from')}
        />
        <DatePicker
          value={dateTo}
          onChange={setDateTo}
          min={dateFrom || undefined}
          placeholder={t('reports.to')}
          ariaLabel={t('reports.to')}
        />
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">{t('reports.totalPurchases')}</p>
            <p className="text-lg font-bold tabular-nums">{formatCurrency(totalAmount)}</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">{t('reports.totalDeposited')}</p>
            <p className="text-lg font-bold text-success tabular-nums">{formatCurrency(totalDeposited)}</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800">
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">{t('reports.pendingBalance')}</p>
            <p className="text-lg font-bold text-destructive tabular-nums">{formatCurrency(payables?.total_payable || 0)}</p>
          </CardContent>
        </Card>
      </div>

      {/* List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-orange-500" />
            {t('reports.pendingPurchases')} ({payables?.count || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isError ? (
            <QueryError onRetry={() => refetch()} />
          ) : isLoading ? (
            <div className="space-y-3">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-16" />
              ))}
            </div>
          ) : payables?.items && payables.items.length > 0 ? (
            <div className="space-y-2">
              {payables.items.map((item) => (
                <div
                  key={item.purchase_id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/30"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{item.provider_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {item.invoice_number ? `${t('reports.invoiceNumber')}: ${item.invoice_number} - ` : ''}
                      {t('reports.purchaseDate')}: {formatDate(item.invoice_date || item.purchase_date)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('reports.registrationDate')}: {formatDate(item.purchase_date)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('reports.purchaseTotalLine', { total: formatCurrency(item.purchase_total), deposited: formatCurrency(item.total_deposited) })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="text-right">
                      <p className="font-bold text-destructive tabular-nums">
                        {formatCurrency(item.remaining)}
                      </p>
                      {item.days_old != null && (
                        <p className={cn(
                          "text-xs font-medium",
                          item.days_old <= 30 ? "text-success" :
                          item.days_old <= 60 ? "text-warning" :
                          item.days_old <= 90 ? "text-orange-700 dark:text-orange-400" : "text-destructive"
                        )}>
                          {item.days_old} {t('reports.days')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              {t('reports.noPendingPurchases')}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
